<template>
  <div class="detalle-servicio-panel">
    <DxTabPanel height="95%" tabs-position="top" icon-position="left" :defer-rendering="false">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" :disabled="false" icon="fa-regular fa-file-lines">
        <servicio-endesa-tab-datos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDatos"></servicio-endesa-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" :disabled="false" icon="fa-regular fa-file-signature">
        <servicio-endesa-tab-plantilla :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabAnexo"></servicio-endesa-tab-plantilla>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" :disabled="false" icon="fa-regular fa-percent">
        <servicio-energia-tab-descuentos :model="model" :tiene-tramites-asociados="tieneTramitesAsociados" ref="tabDescuentos"></servicio-energia-tab-descuentos>
      </DxItem>
      <DxItem :key="4" :title="tituloTab4" class="tab-item" :disabled="false" icon="fa-regular fa-euro-sign">
        <servicio-energia-tab-comisiones-panel :model="model" :is-edit-mode="isEditMode" ref="tabComisiones"></servicio-energia-tab-comisiones-panel>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import ServicioEndesaTabDatos from '@/components/servicios-energia/endesa/ServicioEndesaTabDatos.vue';
import ServicioEndesaTabPlantilla from '@/components/servicios-energia/endesa/SevicioEndesaTabPlantilla.vue';
import ServicioEnergiaTabDescuentos from '@/components/servicios-energia/base/ServicioEnergiaTabDescuentos.vue';
import ServicioEnergiaTabComisionesPanel from '@/components/servicios-energia/base/ServicioEnergiaTabComisionesPanel.vue';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { type CreateServicioEndesaModel, type DetailServicioEndesaModel, type EditServicioEndesaModel, } from '@/.shared/.api';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import type { DxButton } from 'devextreme-vue';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import validationEngine from 'devextreme/ui/validation_engine';
import { useApi } from '@/.shared/.api/integration';
import { toUTCDateOnly } from '@/.shared/helpers/date-parser-helper';
import { ApiError } from '@/.shared/services/http';
import { SERVICE_WITH_BAD_END_DATE } from '@/.shared/errors/codes';

const intl = useIntl();
const api = useApi();
const tabDatos = ref<InstanceType<typeof ServicioEndesaTabDatos>>();
const tabAnexo = ref<InstanceType<typeof ServicioEndesaTabPlantilla>>();
const tabDescuentos = ref<InstanceType<typeof ServicioEnergiaTabDescuentos>>();
const tabComisiones = ref<InstanceType<typeof ServicioEnergiaTabComisionesPanel>>();

const props = defineProps({
  model: {
    type: Object as () => DetailServicioEndesaModel | CreateServicioEndesaModel,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  },
  tieneTramitesAsociados: {
    type: Boolean,
    required: true
  }
});

const tituloTab1 = computed(() => `${intl.i18n.t('servicios.form.datos')}`);
const tituloTab2 = computed(() => `${intl.i18n.t('servicios.form.plantilla_anexo')}`);
const tituloTab3 = computed(() => `${intl.i18n.t('servicios.form.descuentos')}`);
const tituloTab4 = computed(() => `${intl.i18n.t('servicios.form.comisiones')}`);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async (): Promise<boolean> => {
  if (!await validarFormulario()) {
    toastError(intl.i18n.t('servicio_energia.revise_validacion'));
    return false;
  }

  props.model.FechaInicio = toUTCDateOnly(props.model.FechaInicio);
  props.model.FechaFin = toUTCDateOnly(props.model.FechaFin);

  const esPlantilla = tabAnexo.value?.isPlantillaMode() ?? false;
  prepararDatosAnexo(esPlantilla);

  try {
    if (props.isEditMode) {
      await guardarEdicion(esPlantilla);
    } else {
      await guardarCreacion(esPlantilla);
    }
    return true;
  } catch (e: any) {
    mostrarErrorGuardado(e, props.isEditMode);
    return false;
  }
}

async function validarFormulario(): Promise<boolean> {
  const resServicio = validationEngine.validateGroup("validacionServicioEnergia");
  const resTipoPersona = validationEngine.validateGroup("validacionTipoPersona");
  const resFechaFin = validationEngine.validateGroup("validacionFechaFin");
  const resAnexo = await tabAnexo.value?.validate();
  const resDescuentos = await tabDescuentos.value?.validate();

  return (resServicio.isValid && resTipoPersona.isValid && resFechaFin.isValid && !!resAnexo && !!resDescuentos) ?? false;
}

function prepararDatosAnexo(esPlantilla: boolean) {
  if (esPlantilla) {
    const anexoData = tabAnexo.value!.getAnexoData();
    props.model.IdAnexoEmpresaServicios = anexoData.idAnexo;
    props.model.VariablesAnexo = anexoData.variables;
  } else {
    props.model.IdAnexoEmpresaServicios = null;
    props.model.VariablesAnexo = null;
  }
}

async function guardarEdicion(esPlantilla: boolean) {
  if (!esPlantilla && documentoASubir.value) {
    try {
      await api.callStandardApi("putAnexoServicioEnergia", { showLoading: false },
        (props.model as EditServicioEndesaModel).Id, documentoASubir.value.name, documentoASubir.value);
    } catch {
      toastError(intl.i18n.t('servicio_energia.edicion_anexo_fallo'));
    }
  }

  await api.callStandardApi("editServicioEndesa", { showLoading: true }, props.model as EditServicioEndesaModel);
  toastSuccess(intl.i18n.t('servicio_energia.edicion_exito'));
}

async function guardarCreacion(esPlantilla: boolean) {
  let fileName: string | undefined;
  let tempUrl: string | undefined;

  if (!esPlantilla) {
    tempUrl = await api.callStandardApi("postDocumentoTemp", { showLoading: false },
      documentoASubir.value?.name, documentoASubir.value);
    fileName = documentoASubir.value?.name;
  }

  await api.callStandardApi("createServicioEndesa", { showLoading: true },fileName ?? undefined, tempUrl ?? undefined, props.model as CreateServicioEndesaModel);

  toastSuccess(intl.i18n.t('servicio_energia.creacion_exito'));
}

function mostrarErrorGuardado(e: any, esEdicion: boolean) {
  if (esEdicion && e instanceof ApiError
    && e.details?.errorList.length === 1
    && [SERVICE_WITH_BAD_END_DATE].includes(e.details.errorList[0].Code)) {
    toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`));
  } else {
    const clave = esEdicion ? 'servicio_energia.edicion_fallo' : 'servicio_energia.creacion_fallo';
    toastError(intl.i18n.t(clave));
  }
}

const documentoASubir = computed(() => {
  return tabAnexo.value?.documentoASubir;
});

</script>